import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, History } from "lucide-react";
import { Badge } from "./ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = {
  grant: ["Credit given", "bg-teal-500/20 text-teal-300"],
  use: ["Applied to invoice", "bg-sky-500/20 text-sky-300"],
  no_credit: ["No credit", "bg-amber-500/20 text-amber-300"],
  to_decide: ["Still to decide", "bg-slate-600/40 text-slate-300"],
};

/** Who decided what, and when — the full credit trail for a child or family. */
export const PlayerCreditHistory = ({ playerId, parentId, title, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/academy/admin/credits/history`, {
          params: playerId ? { player_id: playerId } : { parent_id: parentId },
          withCredentials: true,
        });
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load the credit history");
      }
    })();
  }, [playerId, parentId]);

  const s = data?.summary;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="credit-history-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <History className="w-4 h-4 text-teal-400" /> Credit history{title ? ` — ${title}` : ""}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"
            data-testid="credit-history-close"><X className="w-5 h-5" /></button>
        </div>

        {!data && !error && (
          <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-teal-400 mx-auto" /></div>
        )}
        {error && <p className="p-6 text-sm text-red-300">{error}</p>}

        {s && (
          <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap gap-3 text-xs">
            <span className="text-slate-400">Credited <span className="font-mono text-teal-300">${s.credited.toFixed(2)}</span></span>
            <span className="text-slate-400">Used <span className="font-mono text-sky-300">${s.used.toFixed(2)}</span></span>
            <span className="text-slate-400">Balance <span className="font-mono text-white" data-testid="credit-history-balance">${s.balance.toFixed(2)}</span></span>
            {s.no_credit_count > 0 && <span className="text-amber-300">{s.no_credit_count} no-credit call(s)</span>}
            {s.to_decide_count > 0 && <span className="text-slate-300">{s.to_decide_count} still to decide</span>}
          </div>
        )}

        <div className="overflow-y-auto p-4 space-y-2">
          {data?.entries?.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="credit-history-empty">
              Nothing recorded yet.
            </p>
          )}
          {(data?.entries || []).map((e) => {
            const [label, cls] = LABEL[e.type] || [e.type, "bg-slate-700/40 text-slate-300"];
            return (
              <div key={e.id} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3"
                data-testid={`credit-entry-${e.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${cls} text-[10px]`}>{label}</Badge>
                  <span className="font-mono text-sm text-white">${Number(e.amount).toFixed(2)}</span>
                  {e.player_name && <span className="text-xs text-slate-400">{e.player_name}</span>}
                  {e.status === "reversed" && (
                    <Badge className="bg-red-500/20 text-red-300 text-[10px]">reversed</Badge>
                  )}
                  <span className="text-[11px] text-slate-500 ml-auto">
                    {e.at ? new Date(e.at).toLocaleString() : "—"}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {e.reason || "No reason recorded"}
                  {e.cancelled_date ? ` · session ${e.cancelled_date}` : ""}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">by {e.by}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
